"use client";

import { useAppToast } from "@/hooks/useAppToast";
import { useMobileOtp } from "@/hooks/useMobileOtp";
import {
  Box,
  Button,
  Center,
  Circle,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
} from "@chakra-ui/react";
import { AppForm, FormField } from "@picovico/ui";
import { catchErrorHandler } from "@picovico/utils";
import { useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { useAppSelector } from "store/hooks";
import { useVerifyMobileVerifiCationOtpMutation } from "store/services/user";
import {
  OTP_SMS_FALLBACK_LABEL,
  OTP_WHATSAPP_DESCRIPTION,
} from "utils/whatsapp";
import * as yup from "yup";

type VerificationStep = "phone" | "otp" | "success";

export default function MobileVerificationForm() {
  const [step, setStep] = useState<VerificationStep>("phone");
  const { error: errorToast } = useAppToast();
  const [verificationId, setVerificationId] = useState<string | null>(null);
  // The code may go on WhatsApp (whatsapp-messaging 02 §10): the OTP step
  // follows the answer's `channel`, with "Send by SMS instead" after 20 s.
  const otp = useMobileOtp();
  const mobileVerificationLoading = otp.isSending;
  const mobile = useAppSelector((state) => state.user.user?.mobile);

  const [verifyOtp, { isLoading: verifyOtpLoading }] =
    useVerifyMobileVerifiCationOtpMutation();

  const handleVerifyClick = async () => {
    const sent = await otp.send();
    if (!sent) return;
    setStep("otp");
    setVerificationId(sent.verificationId);
  };

  const handleSendBySms = async () => {
    const sent = await otp.sendBySms();
    if (sent) setVerificationId(sent.verificationId);
  };

  const handleVerifyOtp = async (values: { otp: string }) => {
    await verifyOtp({
      verification_id: verificationId,
      code: values.otp,
    })
      .unwrap()
      .then(() => {
        setStep("success");
      })
      .catch((error) => {
        catchErrorHandler(error, errorToast);
      });
  };

  return (
    <Box w="full" mx="auto" my={"50px"}>
      <Box>
        <Box mb={4}>
          <Flex align="center" justify="space-between" mb={2}>
            <HStack w="full" spacing={0} justify="space-between">
              <Circle
                size="8"
                bg={
                  step === "phone" || step === "otp" || step === "success"
                    ? "blue.500"
                    : "gray.200"
                }
                color={
                  step === "phone" || step === "otp" || step === "success"
                    ? "white"
                    : "gray.600"
                }
                fontWeight="medium"
                flexShrink={0}
              >
                1
              </Circle>
              <Box h="2px" bg="gray.200" flexGrow={1} mx={2} />
              <Circle
                size="8"
                bg={
                  step === "otp" || step === "success" ? "blue.500" : "gray.200"
                }
                color={
                  step === "otp" || step === "success" ? "white" : "gray.600"
                }
                fontWeight="medium"
                flexShrink={0}
              >
                2
              </Circle>
              <Box h="2px" bg="gray.200" flexGrow={1} mx={2} />
              <Circle
                size="8"
                bg={step === "success" ? "blue.500" : "gray.200"}
                color={step === "success" ? "white" : "gray.600"}
                fontWeight="medium"
                flexShrink={0}
              >
                3
              </Circle>
            </HStack>
          </Flex>
          <Flex justify="space-between" fontSize="xs" color="gray.500">
            <Text
              fontWeight={step === "phone" ? "medium" : "normal"}
              color={step === "phone" ? "gray.800" : "gray.500"}
            >
              Enter Phone
            </Text>
            <Text
              fontWeight={step === "otp" ? "medium" : "normal"}
              color={step === "otp" ? "gray.800" : "gray.500"}
            >
              Verify OTP
            </Text>
            <Text
              fontWeight={step === "success" ? "medium" : "normal"}
              color={step === "success" ? "gray.800" : "gray.500"}
            >
              Complete
            </Text>
          </Flex>
        </Box>
        <Heading size="md">Mobile Verification</Heading>
        <Text color="gray.500" fontSize="sm" mt={1}>
          {step === "phone" &&
            "The number you have saved in the system will be used to send the OTP. If you want to change the number, please change it from the profile."}
          {step === "otp" &&
            (otp.sent?.channel === "whatsapp"
              ? `${OTP_WHATSAPP_DESCRIPTION} ${mobile || "your mobile"}`
              : "Enter the 4-digit code sent to your mobile")}
          {step === "success" && "Your mobile number has been verified"}
        </Text>
      </Box>
      <Box pb="2rem">
        {step === "phone" && (
          <Button
            mt="1rem"
            w="full"
            onClick={handleVerifyClick}
            isDisabled={mobileVerificationLoading || verifyOtpLoading}
            isLoading={mobileVerificationLoading || verifyOtpLoading}
            loadingText="Sending..."
          >
            Verify
          </Button>
        )}
        {step === "otp" && (
          <AppForm
            apiError={null}
            initialValues={{
              otp: "",
            }}
            formProps={{
              padding: "0rem",
              justifyContent: "center",
              alignItems: "center",
            }}
            validationSchema={yup.object().shape({})}
            onSubmit={handleVerifyOtp}
          >
            <FormField
              name="otp"
              label=""
              type="otpfield"
              mt={0}
              mb={0}
              color={"white"}
              mixBlendMode={"difference"}
            />

            <Button
              isLoading={verifyOtpLoading || mobileVerificationLoading}
              isDisabled={verifyOtpLoading || mobileVerificationLoading}
              my="1rem"
              w="full"
              type="submit"
            >
              Save
            </Button>
            <Button
              w="full"
              variant="link"
              colorScheme="blue"
              onClick={handleVerifyClick}
              isDisabled={mobileVerificationLoading || verifyOtpLoading}
            >
              Resend Code
            </Button>
            {otp.smsOffered && (
              <Button
                w="full"
                mt="0.75rem"
                variant="link"
                colorScheme="blue"
                onClick={handleSendBySms}
                isDisabled={mobileVerificationLoading || verifyOtpLoading}
              >
                {OTP_SMS_FALLBACK_LABEL}
              </Button>
            )}
          </AppForm>
        )}

        {step === "success" && (
          <>
            <Center flexDir="column" py={6}>
              <Circle bg="green.100" p={3} mb={4}>
                <Icon as={FiCheckCircle} boxSize={10} color="green.600" />
              </Circle>
              <Heading size="lg" textAlign="center">
                Verification Successful
              </Heading>
              <Text textAlign="center" color="gray.500" mt={2}>
                Your mobile numbe has been verified successfully.
              </Text>
            </Center>
            <Button
              mt="1rem"
              w="full"
              onClick={() => window.location.reload()}
              isDisabled={mobileVerificationLoading}
              isLoading={mobileVerificationLoading}
              loadingText="Sending..."
            >
              See details
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
